import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FAQ_DATA = [
  {
    question: 'How do I set up a monthly Auto-Membership?',
    answer: 'Go to the Membership page, select the "Auto-Membership" tab, and click "Set Up Monthly Membership." You will be taken to a secure Razorpay checkout page where you can fill your details and authorize a recurring eNach mandate via UPI AutoPay, Debit Card, or Net Banking.'
  },
  {
    question: 'How can I cancel my monthly Auto-Membership?',
    answer: (
      <>
        <p className="mb-2">You can cancel your recurring mandate using any of the methods below:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>UPI AutoPay:</strong> Open your UPI app (Google Pay, PhonePe, Paytm, BHIM, etc.) → Go to <em>AutoPay / Mandates</em> → Find the BSM Bhojan Seva Mandal mandate → Tap <strong>Cancel / Revoke</strong>. The mandate will be cancelled immediately.</li>
          <li><strong>Card / Net Banking eNach:</strong> Log in to your bank&apos;s Net Banking or mobile app → Navigate to <em>e-Mandates / Standing Instructions / Recurring Payments</em> → Select the BSM mandate and cancel it. Alternatively, you may contact your bank to revoke the mandate.</li>
          <li><strong>Contact BSM:</strong> Email us at <a href="mailto:bsmbhojansevamandalfoundation@gmail.com" className="text-orange-600 underline">bsmbhojansevamandalfoundation@gmail.com</a> or call <a href="tel:+919081795075" className="text-orange-600 underline">+91 90817 95075</a> with your registered name and mobile number. We will process the cancellation within 2–3 business days.</li>
        </ul>
        <p className="mt-2 text-sm text-amber-700">Note: Cancelling a mandate stops future deductions. It does not refund any payments already made.</p>
      </>
    )
  },
  {
    question: 'Will I get a receipt for my membership?',
    answer: 'Yes. Razorpay sends an automatic payment confirmation email and receipt for every successful membership payment. For 80G tax-benefit receipts, please contact us via email with your membership details and we will issue it within 5–7 business days.'
  },
  {
    question: 'Is my payment information safe?',
    answer: 'Absolutely. We do not store any of your card, UPI, or banking details on our servers. All transactions are processed securely through Razorpay, which is PCI-DSS compliant and regulated by the Reserve Bank of India (RBI).'
  },
  {
    question: 'Can I join membership via UPI or QR code?',
    answer: 'Yes. On the Membership page, select the "Scan QR" tab to reveal our UPI QR code. You can scan it using any UPI app (Google Pay, PhonePe, Paytm, BHIM, etc.) and complete the membership instantly.'
  },
  {
    question: 'What is the minimum membership amount?',
    answer: 'The membership plans starts at just ₹25. For the Auto-Membership monthly plan, the current preset amount is ₹25 per month, which provides quarterly journal access.'
  },
  {
    question: 'Who do I contact for membership issues or refunds?',
    answer: 'For any membership-related issues, duplicate payments, or refund requests, please email us at bsmbhojansevamandalfoundation@gmail.com or call +91 90817 95075. We will review and respond within 2–3 business days. Please see our Refund Policy page for detailed terms.'
  },
  {
    question: 'Can I change the amount of my monthly Auto-Membership?',
    answer: 'Currently, Razorpay eNach mandates do not support in-place amount changes. To change your monthly amount, please cancel your existing mandate first, then set up a new Auto-Membership with the desired amount.'
  }
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="pt-28 pb-20 bg-amber-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="section-title mb-4">Frequently Asked Questions</h1>
        <p className="text-amber-700 text-center mb-10 max-w-2xl mx-auto">
          Find answers to common questions about memberships, cancellations, receipts, and our payment process.
        </p>

        <div className="space-y-4">
          {FAQ_DATA.map((item, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 focus:outline-none"
              >
                <span className="font-semibold text-amber-900 text-base">
                  {item.question}
                </span>
                <motion.span
                  className="text-orange-600 text-xl font-bold flex-shrink-0"
                  animate={{ rotate: openIndex === index ? 45 : 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                >
                  +
                </motion.span>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-5 text-amber-800 leading-relaxed text-sm">
                      {item.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center bg-white rounded-xl border border-amber-200 p-8">
          <h2 className="font-display text-xl text-amber-900 mb-2">Still have questions?</h2>
          <p className="text-amber-700 mb-4 text-sm">
            We are happy to help. Reach out to us directly.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-6 py-2.5 bg-orange-600 text-white font-medium rounded hover:bg-orange-700 transition-colors text-sm"
          >
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
}
